import ReactDOM from "react-dom";
import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Users, Edit, Trash2, Phone, MapPin, Home, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "package_head", "ci", "di", "supervisor", "technician", "helper", "surveyor", "driver", "vehicle_incharge", "warehouse_manager", "finance", "auditor"];
const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700", package_head: "bg-purple-100 text-purple-700",
  ci: "bg-blue-100 text-blue-700", supervisor: "bg-indigo-100 text-indigo-700",
  technician: "bg-green-100 text-green-700", helper: "bg-teal-100 text-teal-700",
  surveyor: "bg-cyan-100 text-cyan-700", driver: "bg-yellow-100 text-yellow-700",
  warehouse_manager: "bg-orange-100 text-orange-700", finance: "bg-pink-100 text-pink-700"
};

// TA-eligible roles that need home GPS
const TA_ELIGIBLE_ROLES = ["technician", "helper", "surveyor", "supervisor"];

// Roles that can have capabilities
const CAPABILITY_ROLES = ["technician", "surveyor", "helper"];

// Available capabilities
const CAPABILITIES = [
  { key: "can_survey", label: "Can Survey", description: "Can perform Survey work" },
  { key: "can_mi", label: "Can do MI", description: "Can perform Meter Installation" },
  { key: "can_dtm", label: "Can do DTM", description: "Can perform DT Metering" },
  { key: "can_dtc", label: "Can do DTC", description: "Can perform DT Commissioning" }
];

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [creating, setCreating] = useState(false);
  const [circles, setCircles] = useState([]);
  const [capturingGps, setCapturingGps] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [form, setForm] = useState({
    mobile: "", full_name: "", role: "technician", circle_name: "",
    division_name: "", password: "admin123", employee_code: "",
    home_gps_lat: "", home_gps_lng: "", home_address: "",
    capabilities: { can_survey: false, can_mi: false, can_dtm: false, can_dtc: false }
  });

  const defaultForm = {
    mobile: "", full_name: "", role: "technician", circle_name: "",
    division_name: "", password: "admin123", employee_code: "",
    home_gps_lat: "", home_gps_lng: "", home_address: "",
    capabilities: { can_survey: false, can_mi: false, can_dtm: false, can_dtc: false }
  };

  // Set default capabilities based on role
  const getDefaultCapabilities = (role) => {
    switch (role) {
      case "technician":
        return { can_survey: true, can_mi: true, can_dtm: true, can_dtc: true };
      case "surveyor":
        return { can_survey: true, can_mi: true, can_dtm: false, can_dtc: false };
      case "helper":
        return { can_survey: false, can_mi: false, can_dtm: false, can_dtc: false };
      default:
        return { can_survey: false, can_mi: false, can_dtm: false, can_dtc: false };
    }
  };

  // Update capabilities when role changes
  const handleRoleChange = (newRole) => {
    setForm(prev => ({
      ...prev,
      role: newRole,
      capabilities: getDefaultCapabilities(newRole)
    }));
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await api.get("/employees", { params });
      setEmployees(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchCircles = async () => {
    try {
      const res = await api.get("/hierarchy/circles");
      setCircles(res.data);
    } catch {}
  };

  useEffect(() => { fetchEmployees(); fetchCircles(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.full_name?.trim()) { toast.error("Employee name is required"); return; }
    if (!form.mobile?.trim()) { toast.error("Mobile number is required"); return; }
    if (form.mobile.length !== 10) { toast.error("Mobile number must be 10 digits"); return; }
    if (!form.role) { toast.error("Role is required"); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      // Convert GPS strings to numbers or null if empty
      payload.home_gps_lat = payload.home_gps_lat ? parseFloat(payload.home_gps_lat) : null;
      payload.home_gps_lng = payload.home_gps_lng ? parseFloat(payload.home_gps_lng) : null;
      // Remove empty strings for optional fields
      if (!payload.circle_name) delete payload.circle_name;
      if (!payload.division_name) delete payload.division_name;
      if (!payload.employee_code) delete payload.employee_code;
      if (!payload.home_address) delete payload.home_address;
      
      await api.post("/employees", payload);
      toast.success("Employee created successfully");
      setShowCreate(false);
      setForm(defaultForm);
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create employee");
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        full_name: form.full_name,
        circle_name: form.circle_name || null,
        division_name: form.division_name || null,
        home_gps_lat: form.home_gps_lat ? parseFloat(form.home_gps_lat) : null,
        home_gps_lng: form.home_gps_lng ? parseFloat(form.home_gps_lng) : null,
        home_address: form.home_address || null,
        capabilities: form.capabilities
      };
      await api.put(`/employees/${editEmployee.id}`, payload);
      toast.success("Employee updated successfully");
      setShowEdit(false);
      setEditEmployee(null);
      setForm(defaultForm);
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update employee");
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (emp) => {
    setEditEmployee(emp);
    setForm({
      mobile: emp.mobile || "",
      full_name: emp.full_name || "",
      role: emp.role || "technician",
      circle_name: emp.circle_name || "",
      division_name: emp.division_name || "",
      password: "",
      employee_code: emp.employee_code || "",
      home_gps_lat: emp.home_gps_lat?.toString() || "",
      home_gps_lng: emp.home_gps_lng?.toString() || "",
      home_address: emp.home_address || "",
      capabilities: emp.capabilities || getDefaultCapabilities(emp.role)
    });
    setShowEdit(true);
  };

  const captureHomeGps = () => {
    setCapturingGps(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm({
            ...form,
            home_gps_lat: position.coords.latitude.toFixed(6),
            home_gps_lng: position.coords.longitude.toFixed(6)
          });
          setCapturingGps(false);
          toast.success("GPS location captured");
        },
        (error) => {
          console.error("GPS Error:", error);
          setCapturingGps(false);
          toast.error("Failed to capture GPS. Please enter manually.");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setCapturingGps(false);
      toast.error("Geolocation not supported");
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this employee?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee deactivated");
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deactivate");
    }
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="employees-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Employees</h1>
          <p className="text-slate-500 text-sm">{total} total employees</p>
        </div>
        {["admin", "ci"].includes(user?.role) && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600" data-testid="create-employee-btn">
            <Plus size={16} />Add Employee
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input type="text" placeholder="Search name or code..." className="field-input pl-8 h-9 text-xs"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && fetchEmployees()} data-testid="emp-search" />
        </div>
        <select className="field-select h-9 text-xs min-w-[160px]" value={roleFilter} onChange={e => { setRoleFilter(e.target.value); fetchEmployees(); }} data-testid="role-filter">
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r.replace("_", " ").toUpperCase()}</option>)}
        </select>
        <button onClick={fetchEmployees} className="px-4 py-2 bg-orange-500 text-white rounded-sm text-xs font-bold hover:bg-orange-600">Search</button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Code</th><th>Name</th><th>Role</th><th>Circle</th><th>Mobile</th><th>Home GPS</th><th>Status</th>
              {["admin", "ci"].includes(user?.role) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i}>{[...Array(8)].map((_, j) => <td key={j}><div className="h-4 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>
              ))
            ) : employees.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400"><Users size={32} className="mx-auto mb-2" />No employees found</td></tr>
            ) : employees.map(emp => (
              <tr key={emp.id}>
                <td><span className="font-mono text-xs font-semibold">{emp.employee_code}</span></td>
                <td className="font-medium text-slate-800">{emp.full_name}</td>
                <td><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${ROLE_COLORS[emp.role] || "bg-slate-100 text-slate-600"}`}>{emp.role}</span></td>
                <td className="text-xs text-slate-600">{emp.circle_name || "—"}</td>
                <td>
                  <span className="font-mono text-xs text-slate-600 flex items-center gap-1">
                    <Phone size={10} />{emp.mobile || "—"}
                  </span>
                </td>
                <td>
                  {TA_ELIGIBLE_ROLES.includes(emp.role) ? (
                    emp.home_gps_lat ? (
                      <span className="text-green-600 flex items-center gap-1 text-[10px]">
                        <Home size={10} />Set
                      </span>
                    ) : (
                      <span className="text-amber-500 flex items-center gap-1 text-[10px]">
                        <MapPin size={10} />Not Set
                      </span>
                    )
                  ) : (
                    <span className="text-slate-300 text-[10px]">N/A</span>
                  )}
                </td>
                <td>
                  <span className={`text-[10px] font-bold uppercase ${emp.status === "active" ? "text-green-600" : "text-red-500"}`}>
                    {emp.status}
                  </span>
                </td>
                {["admin", "ci"].includes(user?.role) && (
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => openEditModal(emp)} className="text-blue-500 hover:text-blue-700 p-1" title="Edit" data-testid={`edit-${emp.id}`}>
                        <Edit size={14} />
                      </button>
                      {user?.role === "admin" && (
                        <button onClick={() => handleDeactivate(emp.id)} className="text-red-500 hover:text-red-700 p-1" title="Deactivate" data-testid={`deactivate-${emp.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>Add Employee</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="field-label">Full Name *</label>
                <input className="field-input" required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="emp-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Mobile *</label>
                  <input className="field-input" required pattern="[0-9]{10}" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="emp-mobile-input" />
                </div>
                <div>
                  <label className="field-label">Employee Code</label>
                  <input className="field-input" placeholder="Auto-generated if blank" value={form.employee_code} onChange={e => setForm({ ...form, employee_code: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="field-label">Role *</label>
                <select className="field-select" required value={form.role} onChange={e => handleRoleChange(e.target.value)} data-testid="emp-role-select">
                  {ROLES.map(r => <option key={r} value={r}>{r.replace("_", " ").toUpperCase()}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Circle</label>
                  <select className="field-select" value={form.circle_name} onChange={e => setForm({ ...form, circle_name: e.target.value })}>
                    <option value="">Select circle</option>
                    {circles.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Password</label>
                  <input className="field-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                </div>
              </div>

              {/* Home GPS Section - Only for TA-eligible roles */}
              {TA_ELIGIBLE_ROLES.includes(form.role) && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Home size={16} className="text-orange-500" />
                      Home Location (for Travel Allowance)
                    </label>
                    <button
                      type="button"
                      onClick={captureHomeGps}
                      disabled={capturingGps}
                      className="px-3 py-1 bg-blue-500 text-white rounded-sm text-xs font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                    >
                      {capturingGps ? <><Loader2 size={12} className="animate-spin" /> Capturing...</> : <><MapPin size={12} /> Capture GPS</>}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="field-label">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        className="field-input"
                        placeholder="25.3176"
                        value={form.home_gps_lat}
                        onChange={e => setForm({ ...form, home_gps_lat: e.target.value })}
                        data-testid="home-lat-input"
                      />
                    </div>
                    <div>
                      <label className="field-label">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        className="field-input"
                        placeholder="82.9739"
                        value={form.home_gps_lng}
                        onChange={e => setForm({ ...form, home_gps_lng: e.target.value })}
                        data-testid="home-lng-input"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="field-label">Home Address</label>
                    <textarea
                      className="field-input"
                      rows={2}
                      placeholder="Enter home address..."
                      value={form.home_address}
                      onChange={e => setForm({ ...form, home_address: e.target.value })}
                      data-testid="home-address-input"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">
                    ⓘ Home GPS is used for Travel Allowance calculation. The shorter distance between Home and Warehouse is used as origin.
                  </p>
                </div>
              )}

              {/* Capabilities Section - For field roles */}
              {CAPABILITY_ROLES.includes(form.role) && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <label className="text-sm font-semibold text-slate-700 mb-3 block">
                    Task Capabilities
                  </label>
                  <p className="text-[10px] text-slate-500 mb-3">
                    Select which tasks this employee can perform (beyond their default role)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {CAPABILITIES.map(cap => (
                      <label key={cap.key} className="flex items-center gap-2 p-2 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.capabilities?.[cap.key] || false}
                          onChange={e => setForm({
                            ...form,
                            capabilities: { ...form.capabilities, [cap.key]: e.target.checked }
                          })}
                          className="rounded border-slate-300"
                          data-testid={`cap-${cap.key}`}
                        />
                        <div>
                          <div className="text-xs font-semibold text-slate-700">{cap.label}</div>
                          <div className="text-[10px] text-slate-400">{cap.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setForm(defaultForm); }} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60">
                  {creating ? "Creating..." : "Add Employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && editEmployee && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>Edit Employee</h3>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-sm mb-4">
                <div className="text-xs text-slate-500">Employee Code</div>
                <div className="font-mono font-bold text-slate-800">{editEmployee.employee_code}</div>
                <div className="text-xs text-slate-500 mt-2">Mobile</div>
                <div className="font-mono text-slate-800">{editEmployee.mobile}</div>
                <div className="text-xs text-slate-500 mt-2">Role</div>
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${ROLE_COLORS[editEmployee.role] || "bg-slate-100 text-slate-600"}`}>{editEmployee.role}</span>
              </div>

              <div>
                <label className="field-label">Full Name *</label>
                <input className="field-input" required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Circle</label>
                  <select className="field-select" value={form.circle_name} onChange={e => setForm({ ...form, circle_name: e.target.value })}>
                    <option value="">Select circle</option>
                    {circles.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Division</label>
                  <input className="field-input" value={form.division_name} onChange={e => setForm({ ...form, division_name: e.target.value })} />
                </div>
              </div>

              {/* Home GPS Section - Only for TA-eligible roles */}
              {TA_ELIGIBLE_ROLES.includes(editEmployee.role) && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Home size={16} className="text-orange-500" />
                      Home Location (for TA)
                    </label>
                    <button
                      type="button"
                      onClick={captureHomeGps}
                      disabled={capturingGps}
                      className="px-3 py-1 bg-blue-500 text-white rounded-sm text-xs font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                    >
                      {capturingGps ? <><Loader2 size={12} className="animate-spin" /> Capturing...</> : <><MapPin size={12} /> Capture GPS</>}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="field-label">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        className="field-input"
                        placeholder="25.3176"
                        value={form.home_gps_lat}
                        onChange={e => setForm({ ...form, home_gps_lat: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="field-label">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        className="field-input"
                        placeholder="82.9739"
                        value={form.home_gps_lng}
                        onChange={e => setForm({ ...form, home_gps_lng: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="field-label">Home Address</label>
                    <textarea
                      className="field-input"
                      rows={2}
                      placeholder="Enter home address..."
                      value={form.home_address}
                      onChange={e => setForm({ ...form, home_address: e.target.value })}
                    />
                  </div>
                  {form.home_gps_lat && form.home_gps_lng && (
                    <div className="mt-2 p-2 bg-green-50 rounded-sm text-xs text-green-700 flex items-center gap-2">
                      <MapPin size={12} />
                      GPS Set: {parseFloat(form.home_gps_lat).toFixed(4)}, {parseFloat(form.home_gps_lng).toFixed(4)}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2">
                    ⓘ Home GPS is used for Travel Allowance calculation. The shorter distance between Home and Warehouse is used as origin.
                  </p>
                </div>
              )}

              {/* Capabilities Section - For field roles (Edit) */}
              {CAPABILITY_ROLES.includes(editEmployee.role) && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <label className="text-sm font-semibold text-slate-700 mb-3 block">
                    Task Capabilities
                  </label>
                  <p className="text-[10px] text-slate-500 mb-3">
                    Select which tasks this employee can perform
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {CAPABILITIES.map(cap => (
                      <label key={cap.key} className="flex items-center gap-2 p-2 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.capabilities?.[cap.key] || false}
                          onChange={e => setForm({
                            ...form,
                            capabilities: { ...form.capabilities, [cap.key]: e.target.checked }
                          })}
                          className="rounded border-slate-300"
                          data-testid={`edit-cap-${cap.key}`}
                        />
                        <div>
                          <div className="text-xs font-semibold text-slate-700">{cap.label}</div>
                          <div className="text-[10px] text-slate-400">{cap.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowEdit(false); setEditEmployee(null); setForm(defaultForm); }} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60">
                  {creating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
